import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#loadDeferredMenu();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    this.#loadDeferredMenu();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => this.#initSwipers(details), 0);
      if (this.classList.contains('header-drawer--mobile')) {
        document.documentElement.setAttribute('scroll-lock', '');
      }
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        if (this.classList.contains('header-drawer--mobile')) {
          document.documentElement.removeAttribute('scroll-lock');
        }
        removeTrapFocus();
        this.#setRootScreenActive();
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

    #setRootScreenActive() {
    const container = this.querySelector('.drawer-push-menu');

    if (!container) return;

    const screens = container.querySelectorAll('.dpm-screen');
    screens.forEach((screen) => {
      screen.classList.remove('is-active');
    });

    const root = container.querySelector('[data-screen="root"]');
    if (root) {
      root.classList.add('is-active');
    }

    container.removeAttribute('data-stack');

    const scrollContainer =
      this.querySelector('.menu-drawer__container') ||
      this.querySelector('.menu-drawer') ||
      container;

    scrollContainer.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  #loadDeferredMenu() {
    const load = () => {
      const container = this.querySelector('[data-deferred-menu-container]');
      const template = this.querySelector('template[data-deferred-menu-template]');

      if (!container || !(template instanceof HTMLTemplateElement)) return;

      container.append(template.content.cloneNode(true));
      template.remove();
      this.#setupAnimatedElementListeners();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', load, { once: true });
    } else {
      load();
    }
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

   #initSwipers(scope) {
    if (typeof window.Swiper === 'undefined') return;

    // Banner carousel (1 slide per view)
    this.#initSwiperByType(scope, 'banner-carousel', {
      slidesPerView: 1,
      loop: true,
      speed: 400,
      autoplay: { delay: 3500, disableOnInteraction: false },
      pagination: { clickable: true }
    });

    // Trending carousel (auto width chips)
    this.#initSwiperByType(scope, 'trending-carousel', {
      slidesPerView: 'auto',
      spaceBetween: 10,
      freeMode: true,
      loop: false,
      speed: 300
    });
  }

  #initSwiperByType(scope, type, options) {
    const roots = scope.querySelectorAll(`[data-swiper="${type}"]`);

    roots.forEach((root) => {
      if (root.dataset.swiperInited === '1') return;

      const el = root.querySelector('.swiper');
      if (!el) return;

      const paginationEl = root.querySelector('.swiper-pagination');

      root.dataset.swiperInited = '1';

      const config = { ...options };

      if (config.pagination) {
        if (paginationEl) config.pagination = { ...config.pagination, el: paginationEl };
        else delete config.pagination;
      }

      // eslint-disable-next-line no-new
      new window.Swiper(el, config);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
